import { actions, elements } from '@ecosystem/newron-design-system';
import React from 'react';
import { MAX_ALLOWED_CUSTOM_CLASSES } from '../../../common/selection.menu/constants';
import { MenuSeparatorComponent } from '../../../common/selection.menu/menu.separator';
import { FilterDefinition } from '../../../types';
import { CustomNumericalClass } from '../../../types/content/custom.types';
import { adjustClassesAfterDeletion, updateLabelErrors } from '../../utils';
import { CustomFeatureClasses } from './customFeatureClasses';
import { AddClassButton } from '../addClassButton';

const { Icon } = elements;
const { IconButton } = actions;

interface NumericFeatureProps {
  selectedFeature: FilterDefinition;
  defaultFeatureClass: CustomNumericalClass;
  featureClasses: CustomNumericalClass[];
  setFeatureClasses: React.Dispatch<React.SetStateAction<CustomNumericalClass[]>>;
}
export const NumericFeature: React.FC<NumericFeatureProps> = ({
  selectedFeature,
  defaultFeatureClass,
  featureClasses,
  setFeatureClasses,
}: NumericFeatureProps) => {
  /**
   * Function to render the custom feature classes
   * @param index the index of the feature class
   * @returns JSX.Element
   */
  const renderFeatureClasses = (index: number, featureClass: CustomNumericalClass) => {
    return (
      <CustomFeatureClasses
        key={`feature_${index}`}
        onFeatureChange={handleOnFeatureChange}
        onLabelChange={handleOnLabelChange}
        index={index}
        featureClasses={featureClasses}
        errorMessage={featureClass.labelError}
      />
    );
  };

  /**
   * Function to handle feature change
   * @param index the index of the feature class
   * @returns
   */
  const handleOnFeatureChange = (index: number) => (value: number) => {
    if (!value) return;
    const nextIndex = index + 1;
    const updatedFeatureClass = { ...featureClasses[index] };
    let updatedNextMinFeatureClass = { ...featureClasses[nextIndex] };
    const copyFeatureClass = [...featureClasses];
    updatedFeatureClass.max = value;
    //auto update next min value, current max value=next min value
    if (featureClasses.length > nextIndex) {
      updatedNextMinFeatureClass = { ...featureClasses[nextIndex] };
      updatedNextMinFeatureClass.min = value;
      copyFeatureClass[nextIndex] = updatedNextMinFeatureClass;
    }
    copyFeatureClass[index] = updatedFeatureClass;
    setFeatureClasses(copyFeatureClass);
  };

  /**
   * Function to handle label change
   * @param index the index of the feature class
   * @returns
   */
  const handleOnLabelChange = (index: number) => (label: string) => {
    const updatedFeatureClass = { ...featureClasses[index] };
    const copyFeatureClass = [...featureClasses];
    updatedFeatureClass.label = label;
    copyFeatureClass[index] = updatedFeatureClass;
    updatedFeatureClasses(copyFeatureClass);
  };

  const addFeatureClass = () => {
    //get previous Max value
    const index = featureClasses.length - 1;
    const prevMax = featureClasses[index].min;
    const newMax = featureClasses[index].min === 0 ? Number(selectedFeature.min) : Number(selectedFeature.max);

    const newFeatureTier: CustomNumericalClass = {
      ...defaultFeatureClass,
      max: Number(newMax.toFixed(2)),
      min: Number(prevMax.toFixed(2)),
      label: '',
    };

    const newMaxTier: CustomNumericalClass = {
      ...defaultFeatureClass,
      max: null,
      min: Number(newMax.toFixed(2)),
      label: '',
    };

    // remove the last element and add the new feature tier and the new max tier
    let newFeatureClasses = [...featureClasses].slice(0, -1);

    newFeatureClasses.push(newFeatureTier, newMaxTier);
    updatedFeatureClasses(newFeatureClasses);
  };

  /**
   * Function to delete a feature class
   * @param index the index of the feature class to be deleted
   */
  const deleteFeatureClass = (index: number) => {
    // adjust the feature classes
    const featureClassToUpdate = adjustClassesAfterDeletion(
      index,
      featureClasses,
      Number(selectedFeature.max)
    ) as CustomNumericalClass[];
    // remove the feature class based on index
    const filteredList = featureClassToUpdate.filter((_, i) => i !== index);
    updatedFeatureClasses(filteredList);
  };

  const updatedFeatureClasses = (featureClasses: CustomNumericalClass[]) => {
    setFeatureClasses(updateLabelErrors(featureClasses));
  };

  return (
    <div>
      {!!featureClasses.length && (
        <div className="pt-8 pb-6 flex-grow-0 modal-body overflow-visible">
          <div className="flex-row gap-2">
            <div className="flex gap-1 self-center">
              <span className="text-heading text-md font-bold">Classes</span>
              <span className="text-inactive text-md">{`(${featureClasses.length})`}</span>
            </div>
          </div>

          <MenuSeparatorComponent key={`seperator_main`} />
          {featureClasses.map((_, index, cp) => {
            return (
              <React.Fragment key={`fr_${index + cp.length}`}>
                {renderFeatureClasses(index, featureClasses[index])}
                {cp.length > 1 ? (
                  <IconButton
                    key={`delete_${index}`}
                    testId={`delete-feature-class-btn-${index}`}
                    onClick={() => deleteFeatureClass(index)}
                  >
                    <Icon.icons.Remove />
                  </IconButton>
                ) : null}
                <MenuSeparatorComponent key={`seperator_${index}`} />
              </React.Fragment>
            );
          })}
          <div className=" flex items-center gap-4 ">
            <div className="flex-grow">
              <div className="flex items-center gap-4">
                <span>
                  <AddClassButton
                    testId={'add-feature-class-btn'}
                    onClick={addFeatureClass}
                    isShowing={featureClasses.length < MAX_ALLOWED_CUSTOM_CLASSES}
                  />
                </span>
              </div>
            </div>
            <div className="flex">
              <div className="pl-4">
                <span>
                  <p className="text-xs text-lighter">lower value</p>
                  <p className="text-sm text-inactive">included</p>
                </span>
              </div>
              <div className="pl-4">
                <p className="text-xs text-inactive">—</p>
              </div>
              <div className="pl-4">
                <span>
                  <p className="text-xs text-lighter">higher value</p>
                  <p className="text-sm text-inactive">excluded</p>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
